import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { UniformSlot } from "@uniformdev/canvas-react";
import { ManifestErrorMessage } from "src/uniform/utils/manifest-error-message";
import { useWAEContextSafe } from "src/uniform/utils/wae-context-provider";

import { checkFeatureFlagGate } from "@/utils/feature-flags";
import { featureFlags, FeatureFlag } from "@/utils/feature-flags";

import FeatureGateOverrideControls, {
    type FeatureGateVariant
} from "./feature-gate-override-controls";

export interface UniformFeatureGateProps {
    featureGateId: string;
    override?: "control" | "treatment";
}

const WRAPPER_STYLES = css({
    position: "relative"
});

const UniformFeatureGate = (props: UniformFeatureGateProps) => {
    const { featureGateId, override } = props;

    const context = useWAEContextSafe();
    const isProduction = context?.wacEnvironment === "production";

    const featureGate = Object.entries(featureFlags).find(
        ([, value]) => value.featureFlagName === featureGateId
    )?.[1];

    const [manualOverride, setManualOverride] =
        useState<FeatureGateVariant | null>(null);

    useEffect(() => {
        setManualOverride(null);
    }, [featureGateId]);

    if (!isProduction) {
        const activeVariant: FeatureGateVariant = (() => {
            if (manualOverride) {
                return manualOverride;
            }
            if (override) {
                return override;
            }
            if (featureGate) {
                return checkFeatureFlagGate(featureGate as FeatureFlag)
                    ? "treatment"
                    : "control";
            }
            return "control";
        })();

        return (
            <div css={WRAPPER_STYLES}>
                <FeatureGateOverrideControls
                    featureGateId={featureGateId}
                    selectedVariant={manualOverride}
                    setSelectedVariant={setManualOverride}
                />
                {!featureGate && <ManifestErrorMessage id={featureGateId} />}
                <UniformSlot name={activeVariant} />
            </div>
        );
    }

    if (!featureGate) {
        return null;
    }

    const showTreatment = checkFeatureFlagGate(featureGate as FeatureFlag);

    return <UniformSlot name={showTreatment ? "treatment" : "control"} />;
};

export default UniformFeatureGate;
